import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Define the blog data type
interface BlogPost {
    title: string;
    content: string[];
    excerpt: string;
    image: string;
    category: string;
}

// Blog data
const blogPosts: Record<string, BlogPost> = {
    'nurturing-your-minds-the-power-of-education': {
        title: 'Nurturing Young Minds: The Power of Education',
        excerpt: 'Education is the foundation of sustainable development and empowerment.',
        image: '/images/blog_education.png',
        category: 'Education',
        content: [
            "Education is more than just a path to academic success; it is a powerful tool for nurturing kids. When we invest in a child's education, we equip them with the skills, knowledge, and confidence they need to navigate the world and pursue their dreams.",

            "The Power of Education",

            "Building Confidence: Education helps children develop self-esteem and confidence. As they master new skills and overcome challenges, they begin to see their capabilities. This newfound confidence encourages them to take on new challenges and believe in their potential.",

            "Thinking Skills: Education fosters critical thinking and problem-solving abilities. Children learn to analyse situations, ask questions, and seek solutions. These skills are essential not only in academic settings but also in everyday life, allowing children to make informed decisions.",

            "Social Awareness: Through education, children are exposed to diverse perspectives and cultures. This awareness fosters empathy and respect for others. An educated child is more likely to become an informed and active member of society.",

            "Preparation for the Future: Education equips children with the tools they need for future success. The skills gained through education, such as communication, teamwork, and time management, are invaluable.",

            "Fostering a Love for Learning",

            "Interactive Learning: Engaging children in hands-on activities and interactive experiences makes learning enjoyable. Whether through science experiments, art projects, or outdoor activities, active participation enhances understanding and retention.",

            "Integrating Technology: In today's digital age, incorporating technology into education can enhance learning. Educational apps, online resources provide diverse opportunities for children to learn in ways that resonate with them.",

            "Encouraging Collaboration: Group projects and collaborative activities promote teamwork and communication skills. When children work together, they learn from each other and develop valuable interpersonal skills.",

            "Celebrating Achievements: Acknowledging and celebrating both small and big achievements boosts motivation and reinforces the value of effort and persistence. Whether through praise, rewards, or simply recognition, celebrating success encourages children to continue striving for their goals.",

            "Nurturing children through education is one of the most significant gifts we can give them. By fostering a supportive environment, encouraging curiosity, and celebrating their achievements, we help children unlock their full potential. Education is not just about acquiring knowledge; it's about equipping children with the skills and confidence to navigate the world and pursue their dreams.",

            "As we invest in the education of our children, we are not only shaping their futures but also creating a more informed and compassionate society. Let's commit to empowering the next generation through education, ensuring they have the tools they need to thrive."
        ]
    },
    'women-empowerment-a-pathway-to-equality-and-progress': {
        title: 'Women Empowerment: A Pathway to Equality and Progress',
        excerpt: 'Learn about the transformative impact of women-led entrepreneurship initiatives.',
        image: '/images/blog_women.png',
        category: 'Empowerment',
        content: [
            "Women empowerment is not just a buzzword; it is a crucial movement aimed at ensuring that women have equal rights, opportunities, and access to resources. Empowering women is essential for fostering a more equitable society and unlocking the potential of half the world's population.",

            "The Importance of Women Empowerment",

            "Equality: At its core, women empowerment is about achieving gender equality. Empowering women is fundamental to ensuring that everyone, regardless of gender, has the same opportunities to succeed.",

            "Economic Growth: Studies show that empowering women has a direct positive impact on economic development. When women participate fully in the workforce, economies thrive.",

            "Health and Education: Empowered women are more likely to invest in their health and the education of their children. This creates a ripple effect, as educated women contribute to healthier families and communities.",

            "Leadership and Representation: Women's voices are essential in leadership and decision-making processes. Diverse perspectives lead to better governance and more comprehensive solutions to societal issues. Empowering women to take on leadership roles ensures that their experiences and needs are represented.",

            "Steps Towards Empowerment",

            "Creating a more equitable society requires concerted efforts at individual, community, and institutional levels. Here are some actionable steps:",

            "Promote Education: Advocate for policies that ensure equal access to education for girls and women. Support initiatives that provide scholarships, mentorship, and resources for students.",

            "Encourage Female Leadership: Encourage women in leadership roles, whether in the workplace or community organizations. This can involve mentorship programs and initiatives aimed at fostering female leaders.",

            "Raise Awareness: Engage in conversations about women's rights and gender equality. Share stories of empowered women and the challenges they face, helping to dismantle stereotypes and promote understanding.",

            "Support Women-Owned Businesses: Support purchasing from women-owned businesses and support initiatives that promote women entrepreneurs. Economic empowerment is key to broader societal change.",

            "Stand Against Violence: Advocate for policies and programs that address gender-based violence. Support organizations working to provide resources and safe spaces for survivors.",

            "Engage Men as Allies: Encourage men to participate in the movement for gender equality. Engaging men as allies can lead to meaningful change and support in challenging stereotypes and promoting equality.",

            "Women empowerment is essential for achieving gender equality and fostering a just, inclusive society. By addressing the challenges women face and taking proactive steps toward empowerment, we can create a world where everyone has the opportunity to thrive. As we celebrate the achievements of women throughout history and in our communities today, let's commit to continuing the fight for equality, ensuring that future generations of women are empowered to pursue their dreams and make their mark on the world. Together, we can build a brighter future for all."
        ]
    },
    'healthcare-for-all-the-path-to-nations-wellbeing': {
        title: "HealthCare for All: The Path to Nation's Well-being",
        excerpt: 'Explore how comprehensive healthcare support strengthens families and building healthier communities.',
        image: '/images/blog_healthcare.png',
        category: 'Healthcare',
        content: [
            "Access to healthcare is a fundamental human right, yet millions around the world still struggle to receive the medical attention they need. The concept of 'healthcare for all' emphasizes the importance of equitable access to quality healthcare services, regardless of a person's socioeconomic status or geographic location.",

            "Why Healthcare for All Matters",

            "A Basic Human Right: Health is an integral part of well-being and quality of life. Access to healthcare allows individuals to maintain their health, seek preventive care, and receive treatment when needed.",

            "Economic Stability: Healthcare costs can be a significant burden on families, often leading to financial strain and even bankruptcy. By providing affordable healthcare options, we can alleviate this burden and allow families to invest in their education, homes, and futures.",

            "Stronger Society: When people are healthy, they are more productive and engaged in their communities. Access to healthcare supports workforce participation and contributes to a stronger, more resilient society.",

            "Barriers to Healthcare",

            "Cost: High healthcare costs remain one of the most significant barriers to access. Many individuals forgo necessary treatments or medications due to financial constraints.",

            "Geographic Disparities: People living in rural or underserved areas often face limited access to healthcare facilities and providers. This can result in delays in treatment and poorer health outcomes.",

            "Lack of Healthcare Support: In many countries, inadequate support prevents individuals from receiving the care they need. Those without proper support often delay seeking treatment, leading to more severe health issues.",

            "Steps Towards Universal Healthcare",

            "Community Health Programs: Establishing local health initiatives can help bridge gaps in access. Programs offering preventive care, health education, and outreach services can reach underserved populations effectively.",

            "Telehealth Services: Expanding telehealth options can improve access for those in remote areas or with mobility challenges. Virtual consultations make it easier for individuals to connect with healthcare providers without the barriers of travel.",

            "Education and Awareness: Raising awareness about available healthcare services and resources is crucial. Informing communities about their rights and options can empower individuals to seek care when needed.",

            "Collaboration with Stakeholders: Building partnerships between governments, non-profits, and healthcare providers can enhance the effectiveness of healthcare initiatives. Collaborative efforts can lead to innovative solutions that address community-specific needs.",

            "Achieving healthcare for all is not just a goal but a necessity for building healthier, more equitable societies. By addressing the barriers to access and implementing comprehensive healthcare solutions, we can ensure that everyone has the opportunity to live a healthy and fulfilling life. Together, we can create a future where quality healthcare is accessible to all, regardless of their circumstances."
        ]
    }
};

const BlogDetail: React.FC = () => {
    const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();

    // Get the blog post based on the slug
    const blog = slug ? blogPosts[slug] : null;

    // If blog post not found, redirect to 404 or blog list
    if (!blog) {
        navigate('/blog');
        return null;
    }

    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4 max-w-4xl">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-primary hover:text-primary-dark mb-8 transition-colors duration-200"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Blogs
                </button>

                {/* Blog Content */}
                <article className="prose max-w-none">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                        {blog.title}
                    </h1>

                    <div className="space-y-6 text-gray-700 leading-relaxed">
                        {blog.content.map((paragraph, index) => {
                            // Check if the paragraph is a heading
                            if (paragraph.endsWith(':')) {
                                return (
                                    <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                                        {paragraph}
                                    </h2>
                                );
                            }

                            // Check if the paragraph is a subheading
                            if (paragraph.includes(':')) {
                                const [title, ...rest] = paragraph.split(':');
                                return (
                                    <div key={index}>
                                        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                                            {title}:
                                        </h3>
                                        <p>{rest.join(':').trim()}</p>
                                    </div>
                                );
                            }

                            // Regular paragraph
                            return <p key={index}>{paragraph}</p>;
                        })}
                    </div>
                </article>
            </div>
        </section>
    );
};

export default BlogDetail;
